import { Button, Modal as AntdModal, type ModalProps } from "antd";

import { cn } from "@/lib/cn";

/** 平台 Modal 扩展 props（覆盖 antd title/footer，改用统一壳层） */
export type PlatformModalProps = Omit<ModalProps, "title" | "footer"> & {
  title: React.ReactNode;
  titleIcon?: React.ReactNode;
  /** 标题下方副文案（说明、链接等） */
  subtitle?: React.ReactNode;
  /** undefined=默认确定/取消；null=无底栏；ReactNode=自定义 */
  footer?: React.ReactNode | null;
  /** 默认底栏左侧区域 */
  footerExtra?: React.ReactNode;
  /** 滚动内容区额外 class */
  contentClassName?: string;
  /** 标题右侧额外区域 */
  titleExtra?: React.ReactNode;
  /** 确定按钮图标 */
  confirmIcon?: React.ReactNode;
};

/** 平台统一 Modal：渐变标题栏 + 可滚动内容 + 固定底栏 */
export const Modal = ({
  children,
  title,
  titleIcon,
  subtitle,
  titleExtra,
  footer,
  footerExtra,
  contentClassName,
  styles: propStyles,
  okText,
  cancelText,
  confirmLoading,
  onCancel,
  onOk,
  confirmIcon,
  classNames: propClassNames,
  ...restProps
}: PlatformModalProps) => {
  // 是否渲染底栏：null/false 隐藏；undefined 用默认按钮；其余为自定义
  const showDefaultFooter = footer === undefined;
  const showCustomFooter = footer != null && footer !== false;
  const hasFooter = showDefaultFooter || showCustomFooter;

  return (
    <AntdModal
      {...restProps}
      maskClosable={false}
      title={null}
      footer={null}
      onCancel={onCancel}
      onOk={onOk}
      classNames={{
        ...propClassNames,
        content: cn("overflow-hidden!", propClassNames?.content),
      }}
      styles={{
        ...propStyles,
        content: {
          padding: 0,
          maxHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: 10,
          ...propStyles?.content,
        },
        body: {
          padding: 0,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          ...propStyles?.body,
        },
      }}
    >
      {/* 标题栏：不参与滚动，右侧预留给 antd 关闭按钮 */}
      <div className="shrink-0 border-b border-slate-200 py-4 px-5 bg-linear-to-r from-slate-50 to-orange-50/70">
        <div className="flex items-center justify-between gap-3 pr-8">
          {titleIcon && titleIcon}
          <div className="flex-1 min-w-0">
            <div
              className={cn(
                typeof title === "string" && "font-semibold text-lg text-slate-900 leading-snug",
              )}
            >
              {title}
            </div>
          </div>

          {titleExtra && <div className="shrink-0">{titleExtra}</div>}
        </div>
        {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      </div>
      {/* 内容区：唯一滚动容器，白底 */}
      <div
        className={cn(
          "flex-1 min-h-0 overflow-y-auto  p-4",
          !hasFooter && "rounded-b-[10px]",
          contentClassName,
        )}
      >
        {children}
      </div>
      {/* 默认底栏：左 footerExtra + 右确定/取消 */}
      {showDefaultFooter && (
        <div className="shrink-0 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-[10px] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">{footerExtra}</div>
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <Button onClick={onCancel}>{cancelText ?? "取消"}</Button>
            <Button
              type="primary"
              icon={confirmIcon ?? null}
              onClick={onOk}
              loading={confirmLoading}
            >
              {okText ?? "确定"}
            </Button>
          </div>
        </div>
      )}
      {/* 自定义底栏：左右布局由调用方自行组织 */}
      {showCustomFooter && (
        <div className="shrink-0 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-[10px]">
          {footer}
        </div>
      )}
    </AntdModal>
  );
};

export default Modal;
